import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import InfoIcon from '@mui/icons-material/Info';
import { Grid, TableRow, Tooltip, Typography } from '@mui/material';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import styled from 'styled-components';
import { ClickEvent } from '../../../../common-types';
import { SparkChartComponent } from '../../../common/components/charts/SparkChartComponent';
import { FixedWidthGap } from '../../../common/components/layout';
import {
  getSelectMenuItems,
  InvisibleSelect,
} from '../../../common/components/utils/Select';
import {
  MetricGroupId,
  MetricId,
} from '../../../common/graphql/generated/graphql-sdk';
import {
  darkTeal,
  mediumGray,
  pbsRowHoverColorLevel1,
  pbsRowHoverColorLevel2,
  pbsSegBgColorLevel1,
  pbsSegBgColorLevel2,
  teal2,
} from '../../../common/theme/color-constants';
import { formatSegmentLabel } from '../../../common/utilFunctions/formatters';
import { useLegacyDomainScopedStoresContext } from '../../../common/v2/context/contexts';
import { GlobalSegmentHandle } from '../../../common/v2/context/types';
import { DataFields } from '../../../common/v2/types';
import { BalanceSheetStore } from './balance-sheet-store';
import { getSortedSegmentRows } from './BalanceSheetSegmentationRows';
import {
  renderPbsTableCell,
  StyledSegParentGrid,
  StyledTableCell,
} from './BalanceSheetTable';
import {
  getFormattedValForBenchmarkHeader,
  getFormattedValForFixedHeader,
  getFormattedValForTimeHeader,
  isConfidentialFormattedVal,
} from './services/pbs-utils';
import { getPbsSegmentMeasureLabel } from './translations';
import {
  MetaDataForRow,
  MetaDataObject,
  PbsCellDrillDownContext,
  PbsServiceResponseData,
  PbsTableHeaderObj,
  PbsTimeHeaderData,
  SegmentValTypes,
} from './types';

export interface PbsSegmentationRowProps<
  MetricGroupIdInstance extends MetricGroupId,
> {
  pbsItem: MetricGroupIdInstance;
  timeHeaderObjs: PbsTableHeaderObj[];
  data: PbsServiceResponseData;
  totalData: PbsServiceResponseData;
  metaDataForRow?: {
    meta: MetaDataForRow;
    segments?: Record<string, { meta: MetaDataForRow }>;
  };
  fixedHeaderObjs: PbsTableHeaderObj[];
  benchmarkHeaderObjs: PbsTableHeaderObj[];
  isFirstRow: boolean;
  isLastRow: boolean;
  selectedMeasure: MetricId;
  segmentName: string;
  isOnlyRow: boolean;
  balanceSheetStore: BalanceSheetStore;
  openSqlModal: (metaDataObject: MetaDataObject) => void;
  segmentLevel: number;
  segmentationLevel1Handle: GlobalSegmentHandle;
  segmentationLevel2Handle: GlobalSegmentHandle;
  /** Parent segment name when this row is level 2 (for drill-down filters). */
  segmentLevel1Name?: string;
  onCellDrillDown?: (
    context: PbsCellDrillDownContext,
    timeHeadersData?: PbsTimeHeaderData[]
  ) => void;
}

export const StyledTableInfoIcon = styled(InfoIcon)`
  color: ${mediumGray};
  font-size: 1.4em;
  cursor: pointer;
  &:hover {
    color: ${teal2};
  }
`;

const IntercomIconContainer = styled(Grid)`
  position: absolute;
  top: 32%;
  right: 0;
  display: none;
`;

export const MetaDataIcon = styled(StyledTableInfoIcon)`
  visibility: visible;
`;

const getPbsSegColor = (segmentLevel: number) => {
  return segmentLevel === 1 ? pbsSegBgColorLevel1 : pbsSegBgColorLevel2;
};

const getPbsRowHoverColor = (segmentLevel: number) => {
  return segmentLevel === 1 ? pbsRowHoverColorLevel1 : pbsRowHoverColorLevel2;
};

const StyledOnlyTableRow = styled(TableRow)<{ segmentLevel: number }>`
  height: 0;
  background-color: ${(props) => getPbsSegColor(props.segmentLevel)};
  &:hover {
    background-color: ${(props) => getPbsRowHoverColor(props.segmentLevel)};
    ${IntercomIconContainer} {
      display: inline;
    }
    ${MetaDataIcon} {
      visibility: visible;
    }
  }
`;

const StyledFirstTableRow = styled(TableRow)<{ segmentLevel: number }>`
  height: 0;
  background-color: ${(props) => getPbsSegColor(props.segmentLevel)};
  &:hover {
    background-color: ${(props) => getPbsRowHoverColor(props.segmentLevel)};
    ${IntercomIconContainer} {
      display: inline;
    }
    ${MetaDataIcon} {
      visibility: visible;
    }
  }
`;

const StyledMiddleTableRow = styled(TableRow)<{ segmentLevel: number }>`
  height: 0;
  background-color: ${(props) => getPbsSegColor(props.segmentLevel)};
  &:hover {
    background-color: ${(props) => getPbsRowHoverColor(props.segmentLevel)};
    ${IntercomIconContainer} {
      display: inline;
    }
    ${MetaDataIcon} {
      visibility: visible;
    }
  }
`;

const StyledLastTableRow = styled(TableRow)<{ segmentLevel: number }>`
  height: 0;
  background-color: ${(props) => getPbsSegColor(props.segmentLevel)};

  &:hover {
    background-color: ${(props) => getPbsRowHoverColor(props.segmentLevel)};
    ${IntercomIconContainer} {
      display: inline;
    }
    ${MetaDataIcon} {
      visibility: visible;
    }
  }
`;

export const PbsSegmentationRow = observer(
  (props: PbsSegmentationRowProps<MetricGroupId>) => {
    const {
      pbsItem,
      timeHeaderObjs,
      data,
      totalData,
      fixedHeaderObjs,
      benchmarkHeaderObjs,
      isFirstRow,
      isLastRow,
      selectedMeasure,
      segmentName,
      isOnlyRow,
      balanceSheetStore,
      metaDataForRow,
      openSqlModal,
      segmentLevel,
      segmentationLevel1Handle,
      segmentationLevel2Handle,
      segmentLevel1Name,
      onCellDrillDown,
    } = props;
    const legacyDomainScopedStores = useLegacyDomainScopedStoresContext();
    const {
      permissionsStore,
      metricStore,
      companySettingsStore,
      evaluationStore,
      filterStore,
    } = legacyDomainScopedStores;
    const showMetaData = permissionsStore.canManageAllDomains;
    const sparkChart = (
      dateValueObjs: Record<string, number>,
      metaDataObject: MetaDataObject | null
    ) => {
      return (
        <StyledTableCell
          style={{ paddingLeft: '1em' }}
          align="right"
          $canBeHighlighted
        >
          <SparkChartComponent
            dateValueObjs={dateValueObjs}
            customWidth={'3vw'}
          />
          {showMetaData && metaDataObject && (
            <Tooltip
              title={'Show Sql'}
              placement="top"
              onClick={(e: ClickEvent) => {
                e.stopPropagation();
                openSqlModal(metaDataObject);
              }}
            >
              <MetaDataIcon />
            </Tooltip>
          )}
        </StyledTableCell>
      );
    };

    const {
      segmentMeasures,
      selectedSegmentLevel1Measure,
      setSelectedSegmentLevel1Measure,
      selectedSegmentLevel2Measure,
      setSelectedSegmentLevel2Measure,
      isSegmentationPercAllowed,
    } = balanceSheetStore;
    const segMeasures = segmentMeasures.map((sm) => {
      return {
        label: getPbsSegmentMeasureLabel(sm),
        value: sm,
        key: sm,
      };
    });
    const showHcPercentToggle = isSegmentationPercAllowed(selectedMeasure);
    const selectedSegmentMeasure =
      segmentLevel === 1
        ? selectedSegmentLevel1Measure
        : selectedSegmentLevel2Measure;
    const setSelectedSegmentMeasure =
      segmentLevel === 1
        ? setSelectedSegmentLevel1Measure
        : setSelectedSegmentLevel2Measure;
    const isPercentage =
      isSegmentationPercAllowed(selectedMeasure) &&
      selectedSegmentMeasure === SegmentValTypes.PERCENTAGE_OF_TOTAL;

    const [segmentationLevel1State] = segmentationLevel1Handle;
    const { items: segmentationLevel1 } = segmentationLevel1State;
    const [segmentationLevel2State] = segmentationLevel2Handle;
    const { items: segmentationLevel2 } = segmentationLevel2State;
    const segmentLevel1Dimension = {
      dataType: segmentationLevel1[0]?.dataType,
      dataField: segmentationLevel1[0]?.property as DataFields,
    };
    const segmentLevel2Dimension =
      segmentationLevel2.length > 0
        ? {
            dataType: segmentationLevel2[0]?.dataType,
            dataField: segmentationLevel2[0]?.property as DataFields,
          }
        : null;
    const segmentDimension =
      segmentLevel === 1 ? segmentLevel1Dimension : segmentLevel2Dimension;

    if (!segmentDimension) {
      return null;
    }

    const dateValueObjs: Record<string, number> = {};
    data.timeHeadersData.forEach((t) => {
      const date = t.dateRange[1];
      const valueForDate = t?.data?.[selectedMeasure] ?? null;
      dateValueObjs[date] = valueForDate ?? 0;
    });

    const [showSecondSegment, setShowSecondSegment] = useState<boolean>(false);
    const mainComponent = () => {
      const currMetaDataObject =
        metaDataForRow?.meta?.[selectedMeasure]?.overtime ?? null;
      return (
        <>
          <StyledTableCell
            component="th"
            scope="row"
            color={darkTeal}
            style={{ maxWidth: '16rem' }}
          >
            <StyledSegParentGrid $isSegmented={showSecondSegment}>
              <FixedWidthGap $gap={`${segmentLevel * 2.5}em`} />
              <Grid
                sx={[
                  segmentLevel2Dimension
                    ? {
                        width: '3em',
                        flex: '0 0 auto',
                      }
                    : {
                        width: 0,
                      },
                ]}
              >
                {data.segments ? (
                  <>
                    {showSecondSegment ? (
                      <div
                        onClick={() => setShowSecondSegment(false)}
                        style={{ paddingTop: '7px' }}
                      >
                        <ArrowDropUpIcon
                          color="action"
                          style={{ fontSize: '2.5em' }}
                          data-cy="pbs-segment-render-arrow-up"
                        />
                      </div>
                    ) : (
                      <div
                        onClick={() => setShowSecondSegment(true)}
                        style={{ paddingTop: '5px' }}
                      >
                        <ArrowDropDownIcon
                          color="action"
                          style={{ fontSize: '2.5em' }}
                          data-cy="pbs-segment-render-arrow-down"
                        />
                      </div>
                    )}
                  </>
                ) : null}
              </Grid>
              <Typography
                style={{ paddingLeft: '2.5em', overflow: 'auto' }}
                data-cy="pbs-segment-row-name"
              >
                {formatSegmentLabel(
                  segmentDimension,
                  segmentName,
                  filterStore.employeeData
                )}
              </Typography>
            </StyledSegParentGrid>
            <IntercomIconContainer>
              {showMetaData && currMetaDataObject && (
                <Tooltip
                  title={'Show Sql'}
                  placement="top"
                  onClick={(e: ClickEvent) => {
                    e.stopPropagation();
                    openSqlModal(currMetaDataObject);
                  }}
                >
                  <MetaDataIcon />
                </Tooltip>
              )}
            </IntercomIconContainer>
          </StyledTableCell>
          {showHcPercentToggle && (isFirstRow || isOnlyRow) ? (
            <StyledTableCell align="right">
              <InvisibleSelect
                value={selectedSegmentMeasure}
                onChange={(e) => {
                  e.stopPropagation();
                  setSelectedSegmentMeasure(e.target.value as SegmentValTypes);
                }}
              >
                {getSelectMenuItems(segMeasures)}
              </InvisibleSelect>
            </StyledTableCell>
          ) : (
            <div style={{ width: '100%' }}></div>
          )}
          {timeHeaderObjs.map(({ value: timeHeaderVal }) => {
            const formattedVal = getFormattedValForTimeHeader(
              timeHeaderVal,
              selectedMeasure,
              data,
              totalData,
              isPercentage,
              metricStore
            );
            const timeHeaderData = data.timeHeadersData.find(
              (d) => d.label === timeHeaderVal
            );
            const cellContext: PbsCellDrillDownContext = {
              metricGroupId: pbsItem,
              selectedMeasure,
              headerType: 'time',
              headerValue: timeHeaderVal,
              dateRange: timeHeaderData?.dateRange,
              segmentLevel: segmentLevel as 1 | 2,
              segmentName,
              segmentDimension,
              segmentLevel1Name,
            };
            // Confidential cells must never open the Data Explorer, or drill-down
            // would recover the individual employees the value suppressed.
            const isConfidential = isConfidentialFormattedVal(formattedVal);
            return renderPbsTableCell(
              pbsItem,
              timeHeaderVal,
              formattedVal,
              onCellDrillDown && !isConfidential
                ? (ctx) => onCellDrillDown(ctx, data.timeHeadersData)
                : undefined,
              cellContext
            );
          })}
          {sparkChart(
            dateValueObjs,
            metaDataForRow?.meta?.[selectedMeasure]?.periodTotal ?? null
          )}
          {fixedHeaderObjs.map(({ value: fixedHeaderVal }) => {
            const formattedVal = getFormattedValForFixedHeader(
              fixedHeaderVal,
              selectedMeasure,
              data,
              totalData,
              isPercentage,
              metricStore
            );
            const cellContext: PbsCellDrillDownContext = {
              metricGroupId: pbsItem,
              selectedMeasure,
              headerType: 'fixed',
              headerValue: fixedHeaderVal,
              segmentLevel: segmentLevel as 1 | 2,
              segmentName,
              segmentDimension,
              segmentLevel1Name,
            };
            // Confidential cells must never open the Data Explorer, or drill-down
            // would recover the individual employees the value suppressed.
            const isConfidential = isConfidentialFormattedVal(formattedVal);
            return renderPbsTableCell(
              pbsItem,
              fixedHeaderVal,
              formattedVal,
              isConfidential ? undefined : onCellDrillDown,
              cellContext
            );
          })}
          {benchmarkHeaderObjs.map(({ value: benchmarkHeaderVal }) => {
            const cellData = data.benchmarkHeadersData?.[benchmarkHeaderVal];
            const currMetaDataObject =
              metaDataForRow?.meta?.[selectedMeasure].benchmark ?? null;
            if (!cellData) return null;
            const formattedVal = getFormattedValForBenchmarkHeader(
              benchmarkHeaderVal,
              selectedMeasure,
              data,
              totalData,
              isPercentage,
              metricStore
            );
            return (
              <StyledTableCell
                align="right"
                key={`${benchmarkHeaderVal}${formattedVal}`}
                $canBeHighlighted
              >
                {showMetaData && currMetaDataObject && (
                  <Tooltip
                    title={'Show Sql'}
                    placement="top"
                    onClick={(e: ClickEvent) => {
                      e.stopPropagation();
                      openSqlModal(currMetaDataObject);
                    }}
                  >
                    <MetaDataIcon />
                  </Tooltip>
                )}
                {formattedVal}
              </StyledTableCell>
            );
          })}
        </>
      );
    };

    const renderLevel2Segment = () => {
      const segmentsData = data.segments;
      if (!segmentsData || !showSecondSegment || !segmentLevel2Dimension) {
        return null;
      } else {
        const sortedSegmentRows = getSortedSegmentRows(
          segmentsData.segments,
          segmentLevel2Dimension,
          companySettingsStore,
          evaluationStore
        );
        return sortedSegmentRows.map((segmentRow, i) => {
          const segmentLevel2Name = Object.keys(segmentRow)[0];
          return (
            <PbsSegmentationRow
              key={segmentLevel2Name}
              pbsItem={pbsItem}
              timeHeaderObjs={timeHeaderObjs}
              segmentName={segmentLevel2Name}
              data={Object.values(segmentRow)[0]}
              totalData={segmentsData.totalData}
              metaDataForRow={metaDataForRow?.segments?.[segmentLevel2Name]}
              fixedHeaderObjs={fixedHeaderObjs}
              benchmarkHeaderObjs={benchmarkHeaderObjs}
              selectedMeasure={selectedMeasure}
              isFirstRow={i === 0}
              isLastRow={i === (data.segments?.segments.length ?? 0) - 1}
              isOnlyRow={(data.segments?.segments.length ?? 0) === 1}
              balanceSheetStore={balanceSheetStore}
              openSqlModal={openSqlModal}
              segmentLevel={2}
              segmentationLevel1Handle={segmentationLevel1Handle}
              segmentationLevel2Handle={segmentationLevel2Handle}
              segmentLevel1Name={segmentName}
              onCellDrillDown={onCellDrillDown}
            />
          );
        });
      }
    };

    return (
      <>
        {isOnlyRow ? (
          <StyledOnlyTableRow segmentLevel={segmentLevel}>
            {mainComponent()}
          </StyledOnlyTableRow>
        ) : isFirstRow ? (
          <StyledFirstTableRow segmentLevel={segmentLevel}>
            {mainComponent()}
          </StyledFirstTableRow>
        ) : isLastRow ? (
          <StyledLastTableRow segmentLevel={segmentLevel}>
            {mainComponent()}
          </StyledLastTableRow>
        ) : (
          <StyledMiddleTableRow segmentLevel={segmentLevel}>
            {mainComponent()}
          </StyledMiddleTableRow>
        )}
        {renderLevel2Segment()}
      </>
    );
  }
);
